import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { notificationStreamEventSchema, type NotificationUnreadCount } from '@erp/shared';
import { useSession } from '@/features/auth';
import { notificationKeys } from '@/features/notifications/api/use-notifications';
import { subscribeToEventStream } from '@/lib/event-stream';

const STREAM_PATH = '/api/v1/notifications/stream';
const STREAM_EVENTS = ['ready', 'notification', 'read'] as const;

/**
 * Hält eine Server-Sent-Events-Verbindung offen, solange eine Sitzung besteht.
 * Neue Meldungen aktualisieren den Zähler und melden kritische Fälle als Toast.
 */
export function useNotificationStream(enabled = true): void {
  const { t } = useTranslation();
  const session = useSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const userId = session.data?.id ?? null;

  useEffect(() => {
    if (userId === null || !enabled) {
      return;
    }

    const applyUnreadCount = (unreadCount: number): void => {
      queryClient.setQueryData<NotificationUnreadCount>(notificationKeys.unread, (previous) => ({
        unreadCount,
        criticalCount: previous?.criticalCount ?? 0,
      }));
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    };

    return subscribeToEventStream({
      path: STREAM_PATH,
      events: STREAM_EVENTS,
      onEvent: (event) => {
        const parsed = notificationStreamEventSchema.safeParse(JSON.parse(event.data));
        if (!parsed.success) {
          return;
        }

        // WhatsApp-Ereignisse laufen über denselben Strom, tragen aber keinen Zähler.
        if (
          parsed.data.type !== 'ready' &&
          parsed.data.type !== 'notification' &&
          parsed.data.type !== 'read'
        ) {
          return;
        }

        applyUnreadCount(parsed.data.unreadCount);

        if (parsed.data.type !== 'notification') {
          return;
        }

        const { notification } = parsed.data;
        const showToast =
          notification.severity === 'CRITICAL' || notification.severity === 'SUCCESS';

        if (!showToast) {
          return;
        }

        const options = {
          description: notification.message,
          ...(notification.linkUrl === null
            ? {}
            : {
                action: {
                  label: t('notifications.open'),
                  onClick: () => navigate(notification.linkUrl ?? '/'),
                },
              }),
        };

        if (notification.severity === 'CRITICAL') {
          toast.error(notification.title, options);
          return;
        }
        toast.success(notification.title, options);
      },
    });
  }, [enabled, userId, queryClient, navigate, t]);
}
